<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :feedbackOperationData="feedbackOperationData"
    :hideContentWrapper="!showLoader && !applyErrorState && !getCards.length"
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && getCards.length"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState && getCards.length"
    :footerButtonsParams="footerButtonsData.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="tokenTitle"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, initialRequest)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <component
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :cardFormData="cardFormData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :mdwResponseData="mdwResponseData"
        :clearFormErrors="clearFormErrors"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @updateData="updateDataHandler($event)"
        @newLimitChange="newLimitChange()"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <empty-state-generic
                v-if="!applyErrorState && !showLoader && !getCards.length"
                illustration="emptyState/cards-kamba"
                :title="t('cards.limits.emptyStateTitle')"
                :description="t('cards.limits.emptyStateDescription')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCards, requestCardLimit, editCardLimit, deleteCardLimit, clearFeatureStates } = mapActions(moduleMapper.CARDS_LIMITS)
const { getCards, getCardLimit } = mapGetters(moduleMapper.CARDS_LIMITS)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const tokenTitle = computed(() => {
    if (getCardLimit.value.limit !== -1 && cardFormData.value.newLimit === null) {
        return t('cards.limits.tokenTitleAppendNoLimit')
    }
    return t('cards.limits.tokenTitleAppendNewLimit')
})

/*************************************************
*               Steps/Footer Data                *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
    { description: t('cards.limits.stepperParams.data') },
    { description: t('cards.limits.stepperParams.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/limits/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const cardFormData = ref({
    cardId: null,
    limitsRadio: 'noLimit',
    newLimit: null
})

const mandatoryList = ref([
    'newLimit'
])

const updateDataHandler = ({ fieldName, inputValue }) => {
    cardFormData.value[fieldName] = inputValue
    if (fieldName === 'cardId') requestCardLimitFn()
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequest = async () => {
	showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false

	try {
        await requestCards()
        if (getCards.value.length) {
            cardFormData.value.cardId = getCards.value[0].id
            await requestCardLimit(cardFormData.value.cardId)
        }
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, initialRequest)
	}
}

const requestCardLimitFn = async () => {
	showOverlayLoader.value = true
	showFeedbackToast.value = false

    try {
        await requestCardLimit(cardFormData.value.cardId)
        showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, requestCardLimitFn)
	}
}

const requestEditCardLimit = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    const payload = {
        data: {
            limit: parseInt(cardFormData.value.newLimit)
        },
        id: cardFormData.value.cardId
    }

    try {
        await editCardLimit(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestEditCardLimit)
    }
}

const requestDeleteCardLimit = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    try {
        await deleteCardLimit(cardFormData.value.cardId)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestDeleteCardLimit)
    }
}
/*************************************************
*                                                *
*             Error/Success Handler              *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const feedbackOperationData = ref(null)

const challengeSuccessHandler = (res) => {
    feedbackOperationData.value = {
        status: res.errorLevel,
        code: res.code,
        data: res.data,
        title: res.message
    }

    activeStep.value = 3
    scrollValue.value = 0
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*              Footer Buttons Data               *
*                                                *
*************************************************/
const footerButtonsData = computed(() => {
    return [{
        label: t('cards.charge.kamba.footerButtonsParams.clear'),
        callbackFunction: clearFields,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('cards.charge.kamba.footerButtonsParams.continue'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }, {
        label: t('cards.charge.kamba.footerButtonsParams.goBack'),
        icon: 'arrow-left-full',
        callbackFunction: () => { goToStep(1) },
        step: 2
    },
    {
        label: t('cards.charge.kamba.footerButtonsParams.validate'),
        callbackFunction: confirmChanges,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 2
    }]
})

const clearFields = () => {
    clearFormErrors.value = true
    cardFormData.value = clearObjectStrings(cardFormData.value)

    cardFormData.value = {
        cardId: getCards.value[0].id,
        limitsRadio: 'noLimit',
        newLimit: null
    }

    goToStep(1)
}

const newLimitChange = () => {
    initialRequest()
    clearFields()
}

const validateInputs = () => {
    if (getCardLimit.value.limit !== -1 && cardFormData.value.limitsRadio === 'noLimit') return activeStep.value++

    atLeastOneIsEmpty(cardFormData.value, mandatoryList.value) || cardFormData.value.newLimit <= 0 || cardFormData.value.newLimit === null || (cardFormData.value.newLimit && cardFormData.value.newLimit.length && parseInt(cardFormData.value.newLimit) === 0) ? showMandatoryErrors.value = true : activeStep.value++
}

const confirmChanges = () => {
    /* call edit api when there is no limit originally and then we insert a new limit */
    if (getCardLimit.value.limit === -1 && cardFormData.value.newLimit) return requestEditCardLimit()
    /* call edit api when there is an current limit and we want to change to a new Limit */
    if (getCardLimit.value.limit !== -1 && (getCardLimit.value.limit !== cardFormData.value.newLimit) && cardFormData.value.newLimit !== null) {
		return requestEditCardLimit()
	}
    /* call delete api when there is an current Limit and we change it to null (no Limit) */
    if (getCardLimit.value.limit !== -1 && cardFormData.value.newLimit === null) {
		return requestDeleteCardLimit()
	}
}

initialRequest()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
